import { useList, useCustomMutation, useGo } from '@refinedev/core';
import { useState, useMemo } from 'react';
import {
  Flag,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  User,
  Calendar,
  FileText,
  MessageCircle,
  Star,
  AlertTriangle,
  Image as ImageIcon,
  ExternalLink,
  Ban,
  CheckCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import {
  REPORT_REASON_LABELS,
  REPORT_STATUS_LABELS,
  REPORT_ENTITY_TYPE_LABELS,
  type ContentReport,
  type ReportStatus,
  type ReportEntityType,
} from './types';

/**
 * Иконка статуса
 */
function getStatusIcon(status: ReportStatus) {
  switch (status) {
    case 'PENDING':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'REVIEWING':
      return <Eye className="h-4 w-4 text-blue-500" />;
    case 'RESOLVED':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'DISMISSED':
      return <XCircle className="h-4 w-4 text-muted-foreground" />;
    default:
      return null;
  }
}

/**
 * Цвет badge для статуса
 */
function getStatusVariant(status: ReportStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'PENDING':
      return 'secondary';
    case 'REVIEWING':
      return 'default';
    case 'RESOLVED':
      return 'default';
    case 'DISMISSED':
      return 'outline';
    default:
      return 'secondary';
  }
}

/**
 * Иконка типа контента
 */
function getTypeIcon(type: ReportEntityType) {
  switch (type) {
    case 'POST':
      return <FileText className="h-4 w-4" />;
    case 'COMMENT':
      return <MessageCircle className="h-4 w-4" />;
    case 'REVIEW':
      return <Star className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
}

/**
 * Форматирование даты
 */
function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Получить имя пользователя
 */
function getUserName(user?: { id: number; firstName?: string; lastName?: string; username?: string }): string {
  if (!user) {return 'Неизвестный';}
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  if (name) {return name;}
  if (user.username) {return `@${user.username}`;}
  return `Пользователь #${user.id}`;
}

/**
 * Панель деталей жалобы
 */
function ReportDetailSheet({
  report,
  open,
  onClose,
  onResolve,
  onDismiss,
  isLoading,
}: {
  report: ContentReport | null;
  open: boolean;
  onClose: () => void;
  onResolve: () => void;
  onDismiss: () => void;
  isLoading: boolean;
}) {
  const go = useGo();

  if (!report) {return null;}

  const isPending = report.status === 'PENDING';

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[600px] sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-destructive" />
            Жалоба #{report.id}
          </SheetTitle>
          <SheetDescription>
            Создана {formatDate(report.createdAt)}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-200px)]">
          <div className="space-y-6 pr-4">
            {/* Статус */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Статус</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  {getStatusIcon(report.status)}
                  <Badge variant={getStatusVariant(report.status)}>
                    {REPORT_STATUS_LABELS[report.status]}
                  </Badge>
                </div>
                {report.resolutionReason && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Решение: {report.resolutionReason}
                  </p>
                )}
                {report.resolvedAt && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Решено: {formatDate(report.resolvedAt)}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Тип и причина */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Информация о жалобе
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Тип контента</span>
                  <Badge variant="outline" className="gap-1">
                    {getTypeIcon(report.entityType)}
                    {REPORT_ENTITY_TYPE_LABELS[report.entityType]}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Причина</span>
                  <Badge variant="destructive">
                    {REPORT_REASON_LABELS[report.reason]}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">ID контента</span>
                  <span className="font-mono text-sm">#{report.entityId}</span>
                </div>
              </CardContent>
            </Card>

            {/* Описание жалобы */}
            {report.description && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Описание от жалующегося</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{report.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Жалующийся пользователь */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Жалобу подал
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  {report.reporter?.photoUrl ? (
                    <img
                      src={report.reporter.photoUrl}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <User className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <div className="font-medium">{getUserName(report.reporter)}</div>
                    {report.reporter?.username && (
                      <div className="text-sm text-muted-foreground">
                        @{report.reporter.username}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    onClick={() => go({ to: `/users/show/${report.reporterId}` })}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Контент на который жалуются */}
            {report.reportedContent && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    {getTypeIcon(report.entityType)}
                    Контент жалобы
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Автор контента */}
                  {report.reportedContent.author && (
                    <div className="flex items-center gap-3 pb-3 border-b">
                      {report.reportedContent.author.photoUrl ? (
                        <img
                          src={report.reportedContent.author.photoUrl}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          <User className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-sm">
                          {getUserName(report.reportedContent.author)}
                        </div>
                        <div className="text-xs text-muted-foreground">Автор контента</div>
                      </div>
                    </div>
                  )}

                  {/* Текст контента */}
                  {report.reportedContent.content && (
                    <div
                      className="prose prose-sm dark:prose-invert max-w-none line-clamp-10"
                      dangerouslySetInnerHTML={{ __html: report.reportedContent.content }}
                    />
                  )}

                  {/* Изображения */}
                  {report.reportedContent.images && report.reportedContent.images.length > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                        <ImageIcon className="h-4 w-4" />
                        Изображения ({report.reportedContent.images.length})
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {report.reportedContent.images.map((image) => (
                          <img
                            key={image.id}
                            src={image.url}
                            alt=""
                            className="w-full h-24 object-cover rounded-lg"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Рецензент */}
            {report.reviewer && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Рассмотрел</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Модератор</span>
                    <span className="font-medium">{report.reviewer.username}</span>
                  </div>
                  {report.resolvedAt && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Дата</span>
                      <span>{formatDate(report.resolvedAt)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>

        {/* Кнопки действий */}
        {isPending && (
          <div className="flex gap-2 mt-4">
            <Button
              variant="default"
              className="flex-1"
              onClick={onResolve}
              disabled={isLoading}
            >
              {isLoading ? (
                'Обработка...'
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Удалить контент
                </>
              )}
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={onDismiss}
              disabled={isLoading}
            >
              {isLoading ? (
                'Обработка...'
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Отклонить жалобу
                </>
              )}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * Диалог разрешения жалобы
 */
function ResolveDialog({
  open,
  onClose,
  onConfirm,
  isLoading,
  action,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
  action: 'RESOLVED' | 'DISMISSED';
}) {
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    onConfirm(reason);
    setReason('');
  };

  const isResolve = action === 'RESOLVED';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isResolve ? 'Удалить контент' : 'Отклонить жалобу'}
          </DialogTitle>
          <DialogDescription>
            {isResolve
              ? 'Контент будет удалён/скрыт, жалоба будет отмечена как решённая.'
              : 'Жалоба будет отклонена, контент останется без изменений.'}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label htmlFor="reason">Комментарий модератора (необязательно)</Label>
          <Textarea
            id="reason"
            placeholder={
              isResolve
                ? 'Например: Контент нарушает правила сообщества...'
                : 'Например: Жалоба не обоснована...'
            }
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-2"
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
          <Button
            variant={isResolve ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Обработка...' : (isResolve ? 'Удалить контент' : 'Отклонить')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Подсчёт активных фильтров
 */
function getActiveFiltersCount(statusFilter: string, typeFilter: string, reasonFilter: string): number {
  let count = 0;
  if (statusFilter !== 'all') {count++;}
  if (typeFilter !== 'all') {count++;}
  if (reasonFilter !== 'all') {count++;}
  return count;
}

/**
 * Страница списка жалоб
 */
export const ReportsList = () => {
  useScrollRestoration();

  const [page, setPage] = usePersistedState('reports:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('reports:sort', {
    field: 'createdAt',
    order: 'desc',
  });
  const [search, setSearch] = usePersistedState('reports:search', '');
  const [statusFilter, setStatusFilter] = usePersistedState<string>('reports:status', 'PENDING');
  const [typeFilter, setTypeFilter] = usePersistedState<string>('reports:type', 'all');
  const [reasonFilter, setReasonFilter] = usePersistedState<string>('reports:reason', 'all');
  const [selectedReport, setSelectedReport] = useState<ContentReport | null>(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveAction, setResolveAction] = useState<'RESOLVED' | 'DISMISSED'>('RESOLVED');
  const pageSize = 20;

  const activeFiltersCount = getActiveFiltersCount(statusFilter, typeFilter, reasonFilter);

  // Подготовка фильтров
  const queryFilters = useMemo(() => {
    const result = [];
    if (search) {
      result.push({ field: 'search', operator: 'contains' as const, value: search });
    }
    if (statusFilter !== 'all') {
      result.push({ field: 'status', operator: 'eq' as const, value: statusFilter });
    }
    if (typeFilter !== 'all') {
      result.push({ field: 'entityType', operator: 'eq' as const, value: typeFilter });
    }
    if (reasonFilter !== 'all') {
      result.push({ field: 'reason', operator: 'eq' as const, value: reasonFilter });
    }
    return result;
  }, [search, statusFilter, typeFilter, reasonFilter]);

  // Загрузка жалоб
  const listResult = useList<ContentReport>({
    resource: 'reports',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: queryFilters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listResult.result.data ?? [];
  const total = listResult.result.total ?? 0;
  const isLoading = listResult.query.isLoading;
  const refetchList = listResult.query.refetch;

  // Мутация для resolve
  const resolveMutation = useCustomMutation();
  const resolve = resolveMutation.mutate;
  const isResolving = resolveMutation.mutation.isPending;

  const handleResolve = (status: 'RESOLVED' | 'DISMISSED', reason?: string) => {
    if (!selectedReport) {return;}
    resolve(
      {
        url: `/admin/reports/${selectedReport.id}/resolve`,
        method: 'post',
        values: {
          status,
          resolutionReason: reason || undefined,
        },
      },
      {
        onSuccess: () => {
          setSelectedReport(null);
          setResolveDialogOpen(false);
          refetchList();
        },
      }
    );
  };

  const handleBulkResolve = (reports: ContentReport[], status: 'RESOLVED' | 'DISMISSED') => {
    // Для bulk actions - обрабатываем последовательно
    const resolveSequentially = async () => {
      for (const report of reports) {
        if (report.status === 'PENDING') {
          await new Promise<void>((resolvePromise) => {
            resolve(
              {
                url: `/admin/reports/${report.id}/resolve`,
                method: 'post',
                values: { status },
              },
              {
                onSuccess: () => resolvePromise(),
                onError: () => resolvePromise(),
              }
            );
          });
        }
      }
      refetchList();
    };
    resolveSequentially();
  };

  const handleResetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setTypeFilter('all');
    setReasonFilter('all');
    setPage(1);
  };

  // Bulk actions
  const bulkActions: BulkAction<ContentReport>[] = [
    {
      label: 'Просмотр',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          setSelectedReport(items[0]);
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить контент',
      icon: <Ban className="h-4 w-4" />,
      onClick: (items) => handleBulkResolve(items, 'RESOLVED'),
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
    {
      label: 'Отклонить жалобы',
      icon: <CheckCheck className="h-4 w-4" />,
      onClick: (items) => handleBulkResolve(items, 'DISMISSED'),
      showOnlyWhenSelected: true,
    },
  ];

  // Колонки таблицы
  const columns: Column<ContentReport>[] = [
    {
      key: 'id',
      header: 'ID',
      width: '70px',
      sortable: true,
      render: (value) => (
        <span className="font-mono text-xs text-muted-foreground">#{String(value)}</span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Дата',
      width: '150px',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          {formatDate(String(value))}
        </div>
      ),
    },
    {
      key: 'entityType',
      header: 'Тип',
      width: '120px',
      sortable: true,
      render: (_, report) => (
        <Badge variant="outline" className="gap-1">
          {getTypeIcon(report.entityType)}
          {REPORT_ENTITY_TYPE_LABELS[report.entityType]}
        </Badge>
      ),
    },
    {
      key: 'reason',
      header: 'Причина',
      width: '160px',
      sortable: true,
      render: (_, report) => (
        <Badge variant="secondary">{REPORT_REASON_LABELS[report.reason]}</Badge>
      ),
    },
    {
      key: 'reporter',
      header: 'Жалующийся',
      width: '180px',
      render: (_, report) => (
        <div className="flex items-center gap-2">
          {report.reporter?.photoUrl ? (
            <img
              src={report.reporter.photoUrl}
              alt=""
              className="w-7 h-7 rounded-full object-cover"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
          )}
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-sm truncate">{getUserName(report.reporter)}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Описание',
      render: (_, report) => (
        <div className="max-w-xs">
          {report.description ? (
            <span className="text-sm line-clamp-2">{report.description}</span>
          ) : (
            <span className="text-sm text-muted-foreground italic">Без описания</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      width: '140px',
      sortable: true,
      render: (_, report) => (
        <div className="flex items-center gap-2">
          {getStatusIcon(report.status)}
          <Badge variant={getStatusVariant(report.status)}>
            {REPORT_STATUS_LABELS[report.status]}
          </Badge>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      render: (_, report) => (
        <Button variant="ghost" size="sm" onClick={() => setSelectedReport(report)}>
          <Eye className="h-4 w-4 mr-1" />
          Открыть
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Жалобы на контент"
        description="Рассмотрение жалоб пользователей на посты и комментарии"
        sticky
        icon={<Flag className="h-5 w-5" />}
      />

      {/* Поиск и фильтры */}
      <TableToolbar
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder="Поиск по описанию или пользователю..."
        filtersCount={activeFiltersCount}
        filterTitle="Фильтры жалоб"
        onResetFilters={handleResetFilters}
        filters={
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="status-filter">Статус</Label>
              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  setStatusFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger id="status-filter">
                  <SelectValue placeholder="Статус" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  <SelectItem value="PENDING">На рассмотрении</SelectItem>
                  <SelectItem value="REVIEWING">В процессе</SelectItem>
                  <SelectItem value="RESOLVED">Решено</SelectItem>
                  <SelectItem value="DISMISSED">Отклонено</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="type-filter">Тип контента</Label>
              <Select
                value={typeFilter}
                onValueChange={(v) => {
                  setTypeFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger id="type-filter">
                  <SelectValue placeholder="Тип" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все типы</SelectItem>
                  <SelectItem value="POST">Посты</SelectItem>
                  <SelectItem value="COMMENT">Комментарии</SelectItem>
                  <SelectItem value="REVIEW">Отзывы</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason-filter">Причина</Label>
              <Select
                value={reasonFilter}
                onValueChange={(v) => {
                  setReasonFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger id="reason-filter">
                  <SelectValue placeholder="Причина" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все причины</SelectItem>
                  <SelectItem value="SPAM">Спам</SelectItem>
                  <SelectItem value="HARASSMENT">Оскорбления</SelectItem>
                  <SelectItem value="HATE_SPEECH">Язык ненависти</SelectItem>
                  <SelectItem value="VIOLENCE">Насилие</SelectItem>
                  <SelectItem value="SEXUAL_CONTENT">Сексуальный контент</SelectItem>
                  <SelectItem value="MISINFORMATION">Дезинформация</SelectItem>
                  <SelectItem value="COPYRIGHT">Авторские права</SelectItem>
                  <SelectItem value="OTHER">Другое</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        }
      />

      {/* Таблица */}
      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(report) => setSelectedReport(report)}
        emptyMessage="Нет жалоб"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      {/* Панель деталей */}
      <ReportDetailSheet
        report={selectedReport}
        open={!!selectedReport && !resolveDialogOpen}
        onClose={() => setSelectedReport(null)}
        onResolve={() => {
          setResolveAction('RESOLVED');
          setResolveDialogOpen(true);
        }}
        onDismiss={() => {
          setResolveAction('DISMISSED');
          setResolveDialogOpen(true);
        }}
        isLoading={isResolving}
      />

      {/* Диалог разрешения */}
      <ResolveDialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        onConfirm={(reason) => handleResolve(resolveAction, reason)}
        isLoading={isResolving}
        action={resolveAction}
      />
    </div>
  );
};
